'use client';

import { tv } from 'tailwind-variants';
import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { Professional } from '@medprev-web-app/api-graphql-client';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button/button.component';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface ProfessionalAboutProps {
  professional: Professional;
}

const professionalStyle = tv({
  slots: {
    container: [' overflow-hidden transition-all duration-500 ease-in-out'],
    text: ['text-gray-700 line-clamp-5'],
  },

  variants: {
    showMore: {
      true: {
        text: ['line-clamp-none'],
      },
    },
  },
});

export function ProfessionalAbout({ professional }: ProfessionalAboutProps) {
  const [showMore, setShowMore] = useState(false);
  const [height, setHeight] = useState<number | null>(null);
  const textRef = useRef<HTMLDivElement>(null);

  const { container, text } = professionalStyle();

  const hasThan290
    = typeof professional.description === 'string'
      && professional.description.length > 290;

  const analyticsEventName = showMore ? AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_ABOUT_ME : undefined;

  useEffect(() => {
    if (textRef.current && hasThan290) {
      const itemHeight = textRef.current.scrollHeight;

      setHeight(showMore ? itemHeight : 120);
    }
  }, [hasThan290, showMore]);

  return (
    <div data-testid={DataTestIdEnum.ENTITY_SECTION_ABOUT}>
      <div
        ref={textRef}
        style={{ height: height ? `${height}px` : 'auto' }}
        className={container()}
      >
        <p
          className={text({ showMore })}
          style={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
        >
          {professional.description}
        </p>
      </div>

      {hasThan290 && (
        <Button
          data-testid={DataTestIdEnum.ENTITY_SHOW_MORE_BUTTON}
          variant="text"
          startIcon={showMore ? faMinus : faPlus}
          onClick={() => setShowMore((prev) => !prev)}
          startIconClassName={`w-[0.875rem] h-[0.875rem] transition-transform duration-300 ease-in-out z-0 ${
            showMore ? 'rotate-180' : 'rotate-0'
          }`}
          className="mt-2 text-green-500 hover:underline focus:outline-none transition-colors duration-300 ease-in-out text-left justify-start"
          data-analytics-event={analyticsEventName}
        >
          {showMore ? 'Recolher' : 'Ver mais'}
        </Button>
      )}
    </div>
  );
}
